/**
 * Edit Expense Modal Component
 * Modal/Bottom Sheet for editing an expense entry with category selection
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../store/uiStore';
import { useBudgetStore, type BudgetCategory } from '../../store/budgetStore';
import type { ExpenseEntryRecord } from '../../hooks/useExpenseEntries';
import { useCategoryName } from './CategoryLabel';
import { formatDateForInput, dateInputToISOString } from '../../utils/dateUtils';

// Format number as Brazilian currency
const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Parse Brazilian currency string to number
const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/[^\d]/g, '');
  return parseFloat(cleaned) / 100 || 0;
};

interface EditExpenseModalProps {
  entry: ExpenseEntryRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { amount: number; description: string; date: string; categoryId: string }) => void;
  isPending: boolean;
  categoryIdMap: Map<string, string>;
}

// Helper component for translated category option
function CategoryOption({
  category,
  getBackendCategoryId
}: {
  category: BudgetCategory;
  getBackendCategoryId: (id: string) => string;
}) {
  const categoryName = useCategoryName(category.id, category.name);
  return (
    <option value={getBackendCategoryId(category.id)}>
      {categoryName}
    </option>
  );
}

export function EditExpenseModal({
  entry,
  isOpen,
  onClose,
  onSave,
  isPending,
  categoryIdMap,
}: EditExpenseModalProps) {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const { categories } = useBudgetStore();
  const isDark = theme === 'dark';

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  // Reset form when entry changes
  useEffect(() => {
    if (entry) {
      setAmount(formatCurrency(entry.amount));
      setDescription(entry.description);
      setDate(formatDateForInput(entry.date));
      setSelectedCategoryId(entry.categoryId);
    }
  }, [entry]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseFloat(raw) / 100 || 0;
    setAmount(formatCurrency(numValue));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amountValue = parseCurrency(amount);

    if (amountValue <= 0 || !description.trim() || !selectedCategoryId) {
      return;
    }

    onSave({
      amount: amountValue,
      description: description.trim(),
      date: dateInputToISOString(date),
      categoryId: selectedCategoryId,
    });
  };

  // Get the backend UUID for a frontend category ID (nameKey)
  const getBackendCategoryId = (frontendId: string): string => {
    return categoryIdMap.get(frontendId) || frontendId;
  };

  const isFormValid = parseCurrency(amount) > 0 && description.trim().length > 0 && selectedCategoryId;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="expense-modal-backdrop"
      />

      {/* Modal / Bottom Sheet */}
      <div
        className={`relative w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-2xl shadow-xl ${
          isDark ? 'bg-slate-800' : 'bg-white'
        } animate-slide-up`}
        data-testid="edit-expense-modal"
      >
        {/* Handle bar for mobile */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className={`w-12 h-1.5 rounded-full ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-700/50">
          <h2 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            {t('modals.editExpense.title')}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-slate-700/50 transition-colors ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}
            data-testid="close-expense-modal-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Amount Input */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {t('modals.editExpense.amount')}
            </label>
            <div className="relative">
              <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                R$
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0,00"
                data-testid="edit-expense-amount-input"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                  isDark
                    ? 'bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500'
                    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
              />
            </div>
          </div>

          {/* Description Input */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {t('modals.editExpense.description')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('modals.editExpense.descriptionPlaceholder')}
              data-testid="edit-expense-description-input"
              className={`w-full px-4 py-3 rounded-lg border ${
                isDark
                  ? 'bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500'
                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
              } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
            />
          </div>

          {/* Category Selection */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {t('modals.editExpense.category')}
            </label>
            <select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              data-testid="edit-expense-category-select"
              className={`w-full px-4 py-3 rounded-lg border ${
                isDark
                  ? 'bg-slate-900 border-slate-700 text-slate-100'
                  : 'bg-white border-slate-300 text-slate-900'
              } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
            >
              <option value="">{t('modals.editExpense.selectCategory')}</option>
              {categories.map((category: BudgetCategory) => (
                <CategoryOption key={category.id} category={category} getBackendCategoryId={getBackendCategoryId} />
              ))}
            </select>
          </div>

          {/* Date Input */}
          <div>
            <label className={`text-sm font-medium mb-1 block ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {t('modals.editExpense.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="edit-expense-date-input"
              className={`w-full px-4 py-3 rounded-lg border ${
                isDark
                  ? 'bg-slate-900 border-slate-700 text-slate-100'
                  : 'bg-white border-slate-300 text-slate-900'
              } focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent`}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                isDark
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
              }`}
              data-testid="cancel-expense-edit-button"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isPending}
              data-testid="save-expense-edit-button"
              className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                !isFormValid || isPending
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {isPending ? t('modals.editExpense.saving') : t('modals.editExpense.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
